import React, { useState, useEffect, useCallback, useRef } from "react"
import _ from "lodash"
import { useTranslation } from "providers/i18n"
import { DirectionSearch } from "components/domains/direction/DirectionSearch"
import { FeatureListItem } from "../components/domains/features/ListItem"
import { Box, Avatar, List, useTheme } from "@mui/material"
import { useVenue } from "providers/venue"
import { useMemo } from "react"
import { listItemPropsParserFactory } from "utils/feature"
import {
  CustomIcon,
  KioskIcon,
  ScanQRCodeIcon,
  ShopIcon,
} from "../components/icons"

const ListItemAvatar = ({ icon, path, ...props }) => {
  return (
    <Avatar variant="logo" {...props}>
      {icon ? icon : path ? <CustomIcon path={path} /> : <ShopIcon />}
    </Avatar>
  )
}

const SuggestionListItem = ({ listItemProps, onClick }) => {
  const { primary, secondary, avatarProps } = listItemProps
  return (
    <FeatureListItem
      divider
      primary={primary}
      secondary={secondary}
      onClick={onClick}
      avatar={<ListItemAvatar {...avatarProps} />}
      //TODO: add endAdornment
    />
  )
}

const WAIT_TIME = 400

export const DirectionSearchContainer = (props) => {
  const {
    origin,
    destination,
    onBack = () => {},
    onChange = () => {},
    userLocation = null,
    lastScannedLocation = null,
  } = props
  const { t } = useTranslation({ defaultValue: "" })
  const theme = useTheme()
  const styleOverrides =
    theme.components[`DirectionSearchContainer`]?.styleOverrides || {}
  const { wrapper, suggestionList } = styleOverrides

  const styleUserLocationListItem = useMemo(
    () => theme.components[`UserLocationListItem`]?.styleOverrides || {},
    [theme]
  )

  const { search, getSuggestedSearchItems, isMultiOrdinalVenue } = useVenue()

  const [originValue, setOriginValue] = useState("")
  const [destinationValue, setDestinationValue] = useState("")
  const [focusingInput, setFocusingInput] = useState(null)

  const resultParser = listItemPropsParserFactory({
    t,
    hideLevel: !isMultiOrdinalVenue,
  })

  const originSuggestions = useMemo(() => {
    const features = !_.isEmpty(originValue)
      ? search(originValue)
      : getSuggestedSearchItems()
    return features.map(resultParser)
  }, [originValue, search, getSuggestedSearchItems, resultParser])

  const destinationSuggestions = useMemo(() => {
    const features = !_.isEmpty(destinationValue)
      ? search(destinationValue)
      : getSuggestedSearchItems()
    return features.map(resultParser)
  }, [destinationValue, search, getSuggestedSearchItems, resultParser])

  const userLocations = useMemo(() => {
    // TODO:: move this logic handle by listItemPropsParserFactory function (TBD)
    return _.compact([
      userLocation && {
        id: userLocation?.id,
        primary: t("Your Location"),
        secondary: t("Location of area that you scan QR code"),
        avatarProps: {
          sx: styleUserLocationListItem.icon,
          icon: <KioskIcon />,
        },
      },
      lastScannedLocation && {
        id: lastScannedLocation?.id,
        primary: t("Last Scanned"),
        secondary: t("Your previous location"),
        avatarProps: {
          sx: styleUserLocationListItem.icon,
          icon: <ScanQRCodeIcon />,
        },
      },
    ])
  }, [userLocation, lastScannedLocation, t, styleUserLocationListItem])

  const showUserLocationSuggestion =
    userLocations &&
    userLocations.length > 0 &&
    ((focusingInput === "origin" && originValue === "") ||
      (focusingInput === "destination" && destinationValue === ""))

  const onSelectSuggestion = (target, location) => {
    onChange({
      // Original value
      origin,
      destination,
      // Override with selected value
      [target]: location,
    })
    setFocusingInput(null)
  }

  const handleSwitch = useCallback(() => {
    onChange({
      origin: destination,
      destination: origin,
    })
  }, [onChange, origin, destination])

  const originInputRef = useRef()
  const destinationInputRef = useRef()

  const debounceOriginOnChange = useMemo(
    () =>
      _.debounce((value) => {
        setOriginValue(value)
      }, WAIT_TIME),
    []
  )

  const debounceDestinationOnChange = useMemo(
    () =>
      _.debounce((value) => {
        setDestinationValue(value)
      }, WAIT_TIME),
    []
  )

  const handleChangeOrigin = useCallback(
    (e) => {
      const newValue = e.target.value
      originInputRef.current.value = newValue
      debounceOriginOnChange(newValue)
    },
    [debounceOriginOnChange]
  )

  const handleChangeDestination = useCallback(
    (e) => {
      const newValue = e.target.value
      destinationInputRef.current.value = newValue
      debounceDestinationOnChange(newValue)
    },
    [debounceDestinationOnChange]
  )
  const handleClearOrigin = useCallback(
    (e) => {
      originInputRef.current.value = ""
      setOriginValue("")
      onChange({
        origin: "",
        destination,
      })
    },
    [onChange, destination]
  )

  const handleClearDestination = useCallback(
    (e) => {
      destinationInputRef.current.value = ""
      setDestinationValue("")
      onChange({
        origin,
        destination: "",
      })
    },
    [onChange, origin]
  )

  const originSuggestionAvialable =
    originSuggestions.length > 0 && focusingInput === "origin"
  /**
   * Set originValue|destinationValue with origin's|destination's name
   * Note: Add focusingInput to handle case that input doesnt update when chose the old suggest choice
   */
  useEffect(() => {
    const originValue = t(origin?.name, {
      ns: "imdf",
    })
    setOriginValue(originValue)
    originInputRef.current.value = originValue
  }, [origin, focusingInput, t])

  useEffect(() => {
    const destinationValue = t(destination?.name, {
      ns: "imdf",
    })
    setDestinationValue(destinationValue)
    destinationInputRef.current.value = destinationValue
  }, [destination, focusingInput, t])

  return (
    <Box sx={wrapper}>
      <DirectionSearch
        originInputRef={originInputRef}
        destinationInputRef={destinationInputRef}
        onOriginChange={handleChangeOrigin}
        onDestinationChange={handleChangeDestination}
        onOriginFocus={() => setFocusingInput("origin")}
        onDestinationFocus={() => setFocusingInput("destination")}
        onBack={onBack}
        onSwitch={handleSwitch}
        onClearOrigin={handleClearOrigin}
        onClearDestination={handleClearDestination}
      />

      {(originSuggestionAvialable || showUserLocationSuggestion) && (
        <List sx={suggestionList}>
          {showUserLocationSuggestion &&
            userLocations.map((listItemProps) => (
              <SuggestionListItem
                key={listItemProps.id}
                listItemProps={listItemProps}
                onClick={() => onSelectSuggestion(focusingInput, listItemProps)}
              />
            ))}
          {originSuggestionAvialable &&
            originSuggestions.map((listItemProps) => (
              <SuggestionListItem
                key={listItemProps.id}
                listItemProps={listItemProps}
                onClick={() => onSelectSuggestion(focusingInput, listItemProps)}
              />
            ))}
        </List>
      )}
      {destinationSuggestions.length > 0 && focusingInput === "destination" && (
        <List sx={suggestionList}>
          {destinationSuggestions.map((listItemProps) => (
            <SuggestionListItem
              key={listItemProps.id}
              listItemProps={listItemProps}
              onClick={() => onSelectSuggestion(focusingInput, listItemProps)}
            />
          ))}
        </List>
      )}
    </Box>
  )
}
